import React, { useEffect, useState } from 'react';
import { Button, Table, Modal, Alert, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ContaServicoViewModel, { ContaServico, PaginatedResponse } from '../../../viewmodel/ContaServicoViewModel';
import useUnidadeStore from '../../../stores/unidadeStore';
import AtivacaoContaModal from './AtivacaoContaModal';
import UsuarioViewModel from '../../../viewmodel/usuario/UsuarioViewModel';
import RevogacaoContaModal from './RevogacaoContaModal';
import AlterarResponsavelModal from './AlterarResponsavelModal';
import AlterarSecretModal from './AlterarSecretModal';
import DetalhesContaModal from './DetalhesContaModal';
import PermissoesCustomizadasModal from './PermissoesCustomizadasModal';
import LogsAcessoModal from './LogsAcessoModal';
import OptOutEmailModal from './OptOutEmailModal';

const ConfirmarRevogacaoModal: React.FC<{
    contaId: string | null;
    onConfirm: () => void;
    onClose: () => void;
}> = ({ contaId, onConfirm, onClose }) => {
    return (
        <Modal show={contaId !== null} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Confirmar Revogação</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="danger">
                    <strong>Atenção!</strong> Esta ação é irreversível. Ao revogar esta conta de serviço:
                    <ul>
                        <li>Todos os acessos serão imediatamente suspensos</li>
                        <li>Não será possível reativar a conta</li>
                        <li>Será necessário criar uma nova conta se necessário</li>
                    </ul>
                </Alert>
                <p>Deseja realmente revogar esta conta de serviço?</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button variant="danger" onClick={onConfirm}>
                    Confirmar Revogação
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const CSResponsavelModal: React.FC<{
    responsavel: string | null;
    onClose: () => void;
}> = ({ responsavel, onClose }) => {
    return (
        <Modal show={responsavel !== null} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Responsável</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    {responsavel || 'Nenhum responsável encontrado'}
                </Alert>
            </Modal.Body>
        </Modal>
    );
};

const ContasServicoLista: React.FC = () => {
    const { unidades, fetchUnidades } = useUnidadeStore();
    const [contas, setContas] = useState<PaginatedResponse<ContaServico>>({
        content: [],
        pageable: { pageNumber: 0, pageSize: 10 },
        totalElements: 0,
        totalPages: 0,
        last: false
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [contaParaAtivacao, setContaParaAtivacao] = useState<ContaServico | null>(null);
    const [contaParaRevogacao, setContaParaRevogacao] = useState<string | null>(null);
    const [responsavelSelecionado, setResponsavelSelecionado] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(0);

    const [contaParaRevogar, setContaParaRevogar] = useState<ContaServico | null>(null);
    const [isRevogando, setIsRevogando] = useState<boolean>(false);

    const [contaParaAlterarResponsavel, setContaParaAlterarResponsavel] = useState<{
        id: string;
        descricao: string;
        responsavelAtual?: string;
        isReativacao?: boolean;
    } | null>(null);

    const [contaParaAlterarSecret, setContaParaAlterarSecret] = useState<ContaServico | null>(null);
    const [contaDetalhes, setContaDetalhes] = useState<ContaServico | null>(null);
    const [contaPermissoes, setContaPermissoes] = useState<ContaServico | null>(null);
    const [contaLogs, setContaLogs] = useState<ContaServico | null>(null);
    const [contaOptOut, setContaOptOut] = useState<ContaServico | null>(null);

    // Ordem desejada dos status
    const statusOrder: { [key: string]: number } = {
        'APROVADO': 1,
        'ATIVA': 2,
        'EXPIRADO': 3,
        'REVOGADO': 4
    };

    // Função para ordenar as contas por status
    const ordenarContasPorStatus = (contas: ContaServico[]) => {
        return [...contas].sort((a, b) => {
            const orderA = statusOrder[a.status] || 99;
            const orderB = statusOrder[b.status] || 99;
            return orderA - orderB;
        });
    };

    const handleAlterarResponsavel = async (contaId: string, novoResponsavelId: string | null) => {
        try {
            if (!contaId) {
                throw new Error('ID da conta de serviço ausente para reativação/alteração de responsável');
            }
            await ContaServicoViewModel.alterarResponsavel(contaId, novoResponsavelId);
            const contasAtualizadas = await ContaServicoViewModel.getContasServico(currentPage, 10);
            setContas(contasAtualizadas);
        } catch (error) {
            throw error;
        }
    };

    const handleCancelarExpiracao = async (contaId: string) => {
        try {
            await ContaServicoViewModel.cancelarExpiracao(contaId);
            const contasAtualizadas = await ContaServicoViewModel.getContasServico(currentPage, 10);
            setContas(contasAtualizadas);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Erro ao cancelar expiração');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchUnidades();
                const contasData = await ContaServicoViewModel.getContasServico(currentPage, 10);
                setContas(contasData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Erro desconhecido ao carregar dados');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentPage, fetchUnidades]);

    const getUnidadeNome = (unidadeId: string) => {
        const unidade = unidades.find(u => u.id === unidadeId);
        return unidade ? unidade.nome : unidadeId;
    };

    const handleConfirmarRevogacao = async () => {
        if (!contaParaRevogar) return;

        try {
            setIsRevogando(true);
            await ContaServicoViewModel.revogarConta(contaParaRevogar.clientId);

            // Recarrega a página atual após revogação
            const contasAtualizadas = await ContaServicoViewModel.getContasServico(currentPage, 10);
            setContas(contasAtualizadas);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao revogar conta');
        } finally {
            setIsRevogando(false);
            setContaParaRevogar(null);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const fetchResponsavel = async (responsavelId?: string) => {
        if (!responsavelId) {
            setResponsavelSelecionado('Nenhum responsável definido');
            return;
        }

        try {
            const usuario = await UsuarioViewModel.getUsuario(responsavelId);
            setResponsavelSelecionado(usuario.nome || 'Nome não disponível');
        } catch (error) {
            setResponsavelSelecionado('Erro ao carregar responsável');
        }
    };

    // Função para formatar data no padrão brasileiro
    const formatarDataBrasil = (date: Date | string) => {
        const d = new Date(date);
        return d.toLocaleDateString('pt-BR');
    };

    // Função para calcular dias restantes (considerando expiração máxima de 7 dias)
    const calcularStatusExpiracao = (dataExpiracao: string | Date | null) => {
        if (dataExpiracao === null) {
            return {
                status: 'Não expira',
                classe: 'text-success'
            };
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const expiracao = new Date(dataExpiracao);
        expiracao.setHours(0, 0, 0, 0);

        const diff = expiracao.getTime() - hoje.getTime();
        const diasRestantes = Math.ceil(diff / (1000 * 60 * 60 * 24));

        let status = '';
        let classe = '';

        if (diasRestantes > 6) {
            status = `Expira em ${formatarDataBrasil(dataExpiracao)} (${diasRestantes} dias)`;
            classe = 'text-danger';
        } else if (diasRestantes > 0) {
            status = `Expira em ${formatarDataBrasil(dataExpiracao)} (${diasRestantes} dias)`;
            classe = 'text-danger fw-bold';
        } else if (diasRestantes === 0) {
            status = 'Expira hoje!';
            classe = 'text-danger fw-bold';
        } else {
            status = `Expirado em ${formatarDataBrasil(dataExpiracao)} (há ${Math.abs(diasRestantes)} dias)`;
            classe = 'text-danger';
        }

        return { status, classe };
    };

    if (loading) {
        return <div>Carregando...</div>;
    }

    if (error) {
        return <div className="text-danger">{error}</div>;
    }

    return (
        <>
            <h4>Contas de Serviço</h4>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>client_id</th>
                        <th>Descrição</th>
                        <th>Responsável</th>
                        <th>Unidade</th>
                        <th>Status</th>
                        <th>Expiração</th>
                        <th>Data Criação</th>
                        <th>Data Aprovação</th>
                        <th>Acesso Multi-Unidade</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {ordenarContasPorStatus(contas.content || []).map((conta, index) => {
                        if (!conta) return null;

                        const {
                            clientId,
                            descricao,
                            responsavelId,
                            unidadeId,
                            status,
                            dataExpiracao,
                            dataCriacao,
                            dataAprovacao,
                            acessoMultiUnidade
                        } = conta;

                        // Garante uma key única mesmo se id for nulo/vazio
                        const rowKey = `conta-${index}-${clientId}`;

                        // Formatação de datas
                        const dataCriacaoFormatada = formatarDataBrasil(dataCriacao);
                        const dataAprovacaoFormatada = dataAprovacao
                            ? formatarDataBrasil(dataAprovacao)
                            : '—';

                        // Status de expiração
                        const { status: statusExpiracao, classe: classeExpiracao } = calcularStatusExpiracao(dataExpiracao!);

                        // Estilo condicional para status
                        let fontWeight = 'normal';
                        let color = 'black';
                        let descricaoStatus = '';

                        switch (status) {
                            case 'ATIVA':
                                fontWeight = 'bold';
                                color = 'green';
                                descricaoStatus = 'Pode realizar Integração'
                                break;
                            case 'APROVADO':
                                fontWeight = 'bold';
                                color = 'goldenrod';
                                descricaoStatus = 'Necessita ser Ativada - Se foi ativada, ainda não foi usada'
                                break;
                            case 'EXPIRADO':
                                fontWeight = 'bold';
                                color = 'orange';
                                descricaoStatus = 'Alterações de cadastro do Responsável na AC colocam a Conta em modo de Expiração - Pode-se reativar alterando o Responsável!'
                                break;                            
                            case 'REVOGADO':
                                fontWeight = 'bold';
                                color = 'darkred';
                                descricaoStatus = 'Manualmente revogada por ADM'
                                break;
                            default:
                                fontWeight = 'normal';
                                color = 'black';
                        }

                        const statusStyle = {
                            fontWeight,
                            color,
                            descricaoStatus
                        };

                        // Nome da unidade
                        const unidadeNome = getUnidadeNome(unidadeId);

                        return (
                            <tr key={rowKey}>
                                <td>{clientId}</td>
                                <td>{descricao}</td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="info"
                                        onClick={() => fetchResponsavel(responsavelId)}
                                        disabled={!responsavelId}
                                    >
                                        {responsavelId ? 'Mostrar' : 'N/A'}
                                    </Button>
                                </td>
                                <td>{unidadeNome}</td>
                                <td>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-status`}>{statusStyle.descricaoStatus}</Tooltip>}
                                    >
                                        <span style={statusStyle}>
                                            {status}
                                        </span>
                                    </OverlayTrigger>
                                </td>
                                <td className={classeExpiracao}>
                                    {statusExpiracao}
                                </td>
                                <td>{dataCriacaoFormatada}</td>
                                <td>{dataAprovacaoFormatada}</td>
                                <td>{acessoMultiUnidade ? 'Sim' : 'Não'}</td>
                                <td>
                                    <div className="d-inline-flex gap-1 flex-wrap">
                                        <Button
                                            className="w-auto text-nowrap"
                                            size="sm"
                                            variant="outline-info"
                                            onClick={() => setContaDetalhes(conta)}
                                            title="Ver detalhes completos"
                                        >
                                            🔍 Detalhes
                                        </Button>
                                        <Button
                                            className="w-auto text-nowrap"
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => setContaPermissoes(conta)}
                                            title="Gerenciar permissões customizadas"
                                        >
                                            🔑 Permissões
                                        </Button>
                                        <Button
                                            className="w-auto text-nowrap"
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => setContaLogs(conta)}
                                            title="Ver logs de acesso"
                                        >
                                            📊 Logs
                                        </Button>
                                        <Button
                                            className="w-auto text-nowrap"
                                            size="sm"
                                            variant={conta.optOutEmailIntegracaoBancaAberta ? "outline-warning" : "outline-secondary"}
                                            onClick={() => setContaOptOut(conta)}
                                            title="Preferências de e-mail"
                                        >
                                            🔔 E-mails
                                        </Button>
                                        {(status === 'ATIVA') && (
                                            <Button
                                                className="w-auto text-nowrap"
                                                size="sm"
                                                variant="outline-dark"
                                                onClick={() => setContaParaAlterarSecret(conta)}
                                            >
                                                Alterar secret
                                            </Button>
                                        )}
                                        {(status === 'APROVADO') && (
                                            <Button
                                                className="w-auto text-nowrap"
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => setContaParaAtivacao(conta)}
                                            >
                                                Ativar
                                            </Button>
                                        )}
                                        {(status === 'ATIVA' || status === 'APROVADO') && (
                                            <Button
                                                className="w-auto text-nowrap"
                                                size="sm"
                                                variant="danger"
                                                onClick={() => {
                                                    const contaAtualizada = { ...conta, unidadeId: unidadeNome };
                                                    setContaParaRevogar(contaAtualizada);
                                                }}
                                            >
                                                Revogar
                                            </Button>
                                        )}
                                        {(status === 'ATIVA' || status === 'EXPIRADO') && (
                                            <Button
                                                className="w-auto text-nowrap"
                                                size="sm"
                                                variant={status === 'ATIVA' ? "warning" : "primary"}
                                                onClick={() => setContaParaAlterarResponsavel({
                                                    id: conta.clientId,
                                                    descricao: descricao,
                                                    responsavelAtual: responsavelId,
                                                    isReativacao: status === 'EXPIRADO'
                                                })}
                                            >
                                                {status === 'ATIVA' ? 'Alterar Responsável' : 'Reativar'}
                                            </Button>
                                        )}
                                        {dataExpiracao && (status === 'ATIVA' || status === 'APROVADO') && (
                                            <Button
                                                className="w-auto text-nowrap"
                                                size="sm"
                                                variant="success"
                                                onClick={() => handleCancelarExpiracao(conta.clientId)}
                                                title="Remover ordem de expiração"
                                            >
                                                ❌ Cancelar Expiração
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            {contas.totalPages > 1 && (
                <Pagination>
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />

                    {[...Array(contas.totalPages)].map((_, index) => (
                        <Pagination.Item
                            key={index}
                            active={index === currentPage}
                            onClick={() => handlePageChange(index)}
                        >
                            {index + 1}
                        </Pagination.Item>
                    ))}

                    <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === contas.totalPages - 1}
                    />
                    <Pagination.Last
                        onClick={() => handlePageChange(contas.totalPages - 1)}
                        disabled={currentPage === contas.totalPages - 1}
                    />
                </Pagination>
            )}

            {contaParaAtivacao && (
                <AtivacaoContaModal
                    conta={contaParaAtivacao}
                    onClose={() => setContaParaAtivacao(null)}
                />
            )}

            {contaParaAlterarSecret && (
                <AlterarSecretModal
                    conta={contaParaAlterarSecret}
                    onClose={() => setContaParaAlterarSecret(null)}
                />
            )}

            <RevogacaoContaModal
                show={contaParaRevogar !== null}
                conta={contaParaRevogar ?
                    contaParaRevogar
                    : null}
                onConfirm={handleConfirmarRevogacao}
                onClose={() => setContaParaRevogar(null)}
                isLoading={isRevogando}
            />

            <ConfirmarRevogacaoModal
                contaId={contaParaRevogacao}
                onConfirm={handleConfirmarRevogacao}
                onClose={() => setContaParaRevogacao(null)}
            />

            <CSResponsavelModal
                responsavel={responsavelSelecionado}
                onClose={() => setResponsavelSelecionado(null)}
            />

            <AlterarResponsavelModal
                show={contaParaAlterarResponsavel !== null}
                conta={contaParaAlterarResponsavel}
                onConfirm={(novoResponsavelId) =>
                    handleAlterarResponsavel(contaParaAlterarResponsavel?.id || '', novoResponsavelId)
                }
                onClose={() => setContaParaAlterarResponsavel(null)}
            />

            {contaDetalhes && (
                <DetalhesContaModal
                    show={true}
                    conta={contaDetalhes}
                    onClose={() => setContaDetalhes(null)}
                />
            )}

            {contaPermissoes && (
                <PermissoesCustomizadasModal
                    show={true}
                    conta={contaPermissoes}
                    onClose={() => setContaPermissoes(null)}
                    onRefresh={() => {
                        const fetchData = async () => {
                            const contasData = await ContaServicoViewModel.getContasServico(currentPage, 10);
                            setContas(contasData);
                        };
                        fetchData();
                    }}
                />
            )}

            {contaLogs && (
                <LogsAcessoModal
                    show={true}
                    conta={contaLogs}
                    onClose={() => setContaLogs(null)}
                />
            )}

            {contaOptOut && (
                <OptOutEmailModal
                    show={true}
                    conta={contaOptOut}
                    onClose={() => setContaOptOut(null)}
                    onSuccess={(atualizada) => {
                        setContas(prev => ({
                            ...prev,
                            content: prev.content.map(c => c.clientId === atualizada.clientId ? atualizada : c)
                        }));
                        setContaOptOut(null);
                    }}
                />
            )}
        </>
    );
};

export default ContasServicoLista;
